// Frame-to-frame smoothing for one tracked hand on the camera slide, so the
// numbers on the projector are steady enough to read and talk about.
import { analyzeFingers, pinchCm } from './handPose.js';

/** Frames a new finger count must hold before it replaces the shown one. */
export const SETTLE_FRAMES = 3;
/** Frames a hand may go undetected before its tag lets go. */
export const HOLD_FRAMES = 6;
/** How far the gap moves toward each new measurement (0-1). */
const GAP_EASE = 0.3;
/** How far the drawn landmarks move toward each new detection (0-1). */
const POINT_EASE = 0.5;

/**
 * The next state of one hand, from its previous state and this frame's
 * detection (`{ landmarks, worldLandmarks }`, or null when the hand was not
 * found). Returns null once the hand has been gone for HOLD_FRAMES.
 */
export function stepHand(prev, hand) {
  if (!hand) {
    if (!prev) return null;
    const missing = prev.missing + 1;
    return missing > HOLD_FRAMES ? null : { ...prev, missing };
  }

  const raw = analyzeFingers(hand.landmarks).count;
  const rawGap = hand.worldLandmarks ? pinchCm(hand.worldLandmarks) : null;

  if (!prev) {
    return { landmarks: hand.landmarks, count: raw, candidate: raw, seen: SETTLE_FRAMES, gap: rawGap, missing: 0 };
  }

  const seen = raw === prev.candidate ? prev.seen + 1 : 1;
  const count = seen >= SETTLE_FRAMES ? raw : prev.count;
  const landmarks = hand.landmarks.map((p, i) => {
    const q = prev.landmarks[i] || p;
    return { x: q.x + (p.x - q.x) * POINT_EASE, y: q.y + (p.y - q.y) * POINT_EASE, z: p.z };
  });
  const gap = rawGap == null ? prev.gap : prev.gap == null ? rawGap : prev.gap + (rawGap - prev.gap) * GAP_EASE;

  return { landmarks, count, candidate: raw, seen, gap, missing: 0 };
}
